<template>
  <div class="giftcard-delivery-options" role="radiogroup" :aria-label="label">
    <CartSelectableCard
      v-for="option in options"
      :key="option.id"
      tag="button"
      show-inactive-hover
      role="radio"
      :aria-checked="selected === option.id"
      class="giftcard-delivery-option items-start gap-2.5"
      :class="
        selected === option.id ? 'giftcard-delivery-option--active' : undefined
      "
      :is-active="selected === option.id"
      :data-test-id="`giftcard-delivery-option-${option.id}`"
      @click="selected = option.id"
    >
      <span class="giftcard-delivery-option__radio"></span>
      <div
        class="aspect-square max-h-[50px] shrink-0 self-stretch overflow-hidden rounded-lg"
      >
        <NuxtImg
          :src="option.image"
          :alt="t(`cart.giftcardAddons.${option.id}.title`)"
          width="50"
          height="50"
          class="size-full object-cover"
        />
      </div>
      <span class="giftcard-delivery-option__text">
        <span class="flex items-start justify-between gap-2">
          <span class="giftcard-delivery-option__title">
            {{ t(`cart.giftcardAddons.${option.id}.title`) }}
          </span>
          <span
            v-if="option.price === 0"
            class="giftcard-delivery-option__free"
            :data-test-id="`giftcard-delivery-option-${option.id}-price`"
          >
            {{ t('giftcard.deliveryOptions.free') }}
          </span>
          <span
            v-else
            class="giftcard-delivery-option__price"
            :data-test-id="`giftcard-delivery-option-${option.id}-price`"
          >
            + {{ formatPriceAmount(option.price, locale) }}
          </span>
        </span>
        <span class="giftcard-delivery-option__description">
          {{ t(`cart.giftcardAddons.${option.id}.description`) }}
        </span>
      </span>
    </CartSelectableCard>
  </div>
</template>

<script setup lang="ts">
import CartSelectableCard from '~/components/cart/CartSelectableCard.vue'
import type { GiftcardAddon } from '~/composables/types/cart'
import { cartGetters } from '~/utils/cartGetters'

const { label = undefined } = defineProps<{
  /** Accessible name for the radio group. */
  label?: string
}>()

const selected = defineModel<GiftcardAddon>({ required: true })

const { t, locale } = useI18n()

// Same source of truth as the cart's giftcard option list, so prices and
// visibility stay in sync between the buy box and the cart.
const options = computed(() =>
  cartGetters.getGiftcardAddonList.filter((option) => option.is_visible)
)
</script>

<style lang="css" scoped>
@reference '~/assets/css/tailwind.css';
.giftcard-delivery-options {
  @apply flex flex-col gap-2;
}

.giftcard-delivery-option {
  &__radio {
    @apply min-w-4 h-4 shrink-0 rounded-full border border-gray-300 bg-gray-50 duration-300 box-border;
  }

  &--active {
    .giftcard-delivery-option {
      &__radio {
        @apply border-4 border-primary bg-white;
      }
    }
  }

  &__text {
    @apply flex flex-1 flex-col gap-1;
  }

  &__title {
    @apply text-xs font-medium text-black;
  }

  &__price {
    @apply text-xs font-medium text-black whitespace-nowrap;
  }

  &__free {
    @apply text-xs font-semibold text-teal-900 bg-lime-200 rounded-full px-2 py-0.5 whitespace-nowrap;
  }

  &__description {
    @apply text-gray-600 text-xs font-normal text-left;
  }
}
</style>
